import { ref } from 'vue';

export interface PurchaseFilters {
  regionId: number | null
  unitConfigurations: string[]
  availabilityDates: string[] | undefined
  floors: number[] | undefined
  minPrice: number | undefined
  maxPrice: number | undefined
}

const STORAGE_KEY = 'purchase-filters';

// Fonction utilitaire pour calculer le nombre de filtres secondaires
const calculateSecondaryFiltersCount = (filters: PurchaseFilters): number => {
  let count = 0;
  if (filters.floors && filters.floors.length > 0) count++;
  if (filters.minPrice !== undefined || filters.maxPrice !== undefined) count++;
  return count;
};

// Singleton state - shared across all component instances  
const countSecondaryPurchaseFilters = ref(0);

export function usePurchaseFilters() {
  const loadFiltersFromPurchaseStorage = (): PurchaseFilters => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Mettre à jour le compteur de filtres secondaires
        countSecondaryPurchaseFilters.value = calculateSecondaryFiltersCount(parsed);
        return {
          regionId: parsed.regionId || null,
          unitConfigurations: Array.isArray(parsed.unitConfiguration) ? parsed.unitConfiguration : [],
          availabilityDates: Array.isArray(parsed.availabilityDates) ? parsed.availabilityDates : undefined,
          floors: Array.isArray(parsed.floors) ? parsed.floors : undefined,
          minPrice: parsed.priceRange?.min || undefined,
          maxPrice: parsed.priceRange?.max || undefined
        };
      }
    } catch (error) {
      console.warn('Unable to load filters from localStorage:', error);
    }
    return {
      regionId: null,
      unitConfigurations: [],
      availabilityDates: undefined,
      floors: undefined,
      minPrice: undefined,
      maxPrice: undefined
    };
  };

  const saveFiltersToPurchaseStorage = (filters: Partial<PurchaseFilters>) => {
    try {
      const currentFilters = loadFiltersFromPurchaseStorage();
      const updatedFilters = { ...currentFilters, ...filters };
      
      const storageFormat = {
        regionId: updatedFilters.regionId,
        unitConfiguration: updatedFilters.unitConfigurations,
        availabilityDates: updatedFilters.availabilityDates,
        floors: updatedFilters.floors,
        priceRange: {
          min: updatedFilters.minPrice,
          max: updatedFilters.maxPrice
        }
      };
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storageFormat));
      
      // Mettre à jour le compteur de filtres secondaires
      countSecondaryPurchaseFilters.value = calculateSecondaryFiltersCount(updatedFilters);
    } catch (error) {
      console.warn('Unable to save filters to localStorage:', error);
    }
  };

  return {
    loadFiltersFromPurchaseStorage,
    saveFiltersToPurchaseStorage,
    countSecondaryPurchaseFilters
  };
}
